import React, { useCallback, useMemo, useState } from 'react';

import {
  CreditCardOutlined,
  FileProtectOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  ShareAltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Divider } from 'antd';
import { GrUserAdmin } from 'react-icons/gr';
import { useNavigate } from 'react-router-dom';

import { StyledSideMenu } from './styles';
import { CloseIcon } from '../../../components/atoms/icons/close-icon';
import { GradientButton } from '../../../components/molecules/gradient-button';
import { Menu } from '../../../components/molecules/menu';
import { useAuth } from '../../../hooks/use-auth';
import { useMainNavItems } from '../../../hooks/use-main-nav-items';
import { useAccessStatus } from '../../../hooks/use-subscription-status';
import { useGetProfileQuery } from '../../../store/queries/users';
import { Heading11 } from '../../../theme';
import { bemClassName } from '../../../utils/bem';
import { USER_ROLES } from '../../../utils/constants';
import { LoginModal } from '../login-modal';

const b = bemClassName('side-menu');

const SideMenu = ({ isOpen, onDismiss }) => {
  const navigate = useNavigate();
  const { isAuthenticated, userSignOut } = useAuth();
  const { isOnboardingComplete } = useAccessStatus();
  const { data: profileData } = useGetProfileQuery(undefined, { skip: !isAuthenticated });
  const navItems = useMainNavItems();

  const [isOpenLoginModal, setIsOpenLoginModal] = useState(false);

  const goToOnboarding = useCallback(() => {
    navigate('/onboarding');
  }, [navigate]);

  const openLoginModal = useCallback(() => {
    setIsOpenLoginModal(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setIsOpenLoginModal(false);
  }, []);

  const handleClose = (e) => {
    e.stopPropagation();
    onDismiss();
  };

  const handleAccountItemClick = useCallback(
    (path) => {
      navigate(path);
      onDismiss();
    },
    [navigate, onDismiss],
  );

  const accountItems = useMemo(
    () =>
      [
        profileData?.role === USER_ROLES.admin && {
          key: 'admin',
          label: 'Admin panel',
          icon: <GrUserAdmin />,
          path: '/admin',
        },
        isOnboardingComplete && {
          key: 'profile',
          label: 'Profile',
          icon: <UserOutlined />,
          path: '/user/profile',
        },
        isOnboardingComplete && {
          key: 'settings',
          label: 'Settings',
          icon: <SettingOutlined />,
          path: '/user/settings',
        },
        isOnboardingComplete && {
          key: 'subscription',
          label: 'Subscription',
          icon: <FileProtectOutlined />,
          path: '/user/subscription',
        },
        isOnboardingComplete && {
          key: 'payment',
          label: 'Payment Methods',
          icon: <CreditCardOutlined />,
          path: '/user/payment-methods',
        },
        isOnboardingComplete && {
          key: 'referrals',
          label: 'Referrals',
          icon: <ShareAltOutlined />,
          path: '/user/referrals',
        },
        isOnboardingComplete && {
          key: 'help',
          label: 'Help',
          icon: <QuestionCircleOutlined />,
          path: '/user/help',
        },
      ].filter(Boolean),
    [profileData?.role, isOnboardingComplete],
  );

  return (
    <StyledSideMenu>
      <div className={b({ 'is-open': isOpen })} onClick={handleClose}>
        <div className={b('content')} onClick={(e) => e.stopPropagation()}>
          <button className={b('content', 'close-button')} onClick={handleClose}>
            <CloseIcon />
          </button>

          <Menu
            vertical
            onItemClick={onDismiss}
            items={navItems.map(({ key, label, link, Icon }) => ({ key, label, link, icon: <Icon size={18} /> }))}
          />

          {isAuthenticated && accountItems.length > 0 && (
            <div className={b('content', 'account')}>
              <Divider orientation="left" />
              <div className={b('content', 'account', 'items')}>
                {accountItems.map((item) => (
                  <div
                    key={item.key}
                    className={b('content', 'account', 'item')}
                    onClick={() => handleAccountItemClick(item.path)}
                  >
                    <span className={b('content', 'account', 'icon')}>{item.icon}</span>
                    <Heading11>{item.label}</Heading11>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={b('content', 'auth')}>
            {!isAuthenticated && (
              <div className={b('content', 'auth', 'item')}>
                <Divider orientation="left" />
              </div>
            )}

            {isAuthenticated ? (
              <div className={b('content', 'auth', 'item')}>
                <div className={b('content', 'account', 'item', { logout: true })} onClick={userSignOut}>
                  <span className={b('content', 'account', 'icon')}>
                    <LogoutOutlined />
                  </span>
                  <Heading11>Logout</Heading11>
                </div>
              </div>
            ) : (
              <>
                <div className={b('content', 'auth', 'item')}>
                  <GradientButton onClick={openLoginModal} width={'100%'}>
                    SIGN IN
                  </GradientButton>
                </div>
                <div className={b('content', 'auth', 'item')}>
                  <GradientButton onClick={goToOnboarding} width={'100%'}>
                    SIGN UP
                  </GradientButton>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <LoginModal isOpen={isOpenLoginModal} onClose={closeLoginModal} />
    </StyledSideMenu>
  );
};

export default SideMenu;
